"use client";

import { useActionsTrack } from "@/_old/features/analytics";
import BrunaAvatar from "@/assets/bruna-avatar.png";
import { BadgeIA } from "@/core/components/layout/badge-ia";
import { CarouselInsightsItem } from "@/core/components/layout/carousel-insights/components/carousel-insights-item";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/core/components/ui/avatar";
import { Button } from "@/core/components/ui/button";
import { cn } from "@/core/utils";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader,
  Play,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useExceededCreditsCard } from "../hooks/exceeded-credits-card.hooks";
import { ExceededCreditsModalDetails } from "./exceeded-credits-modal-details";
import { CarouselItem } from "@/core/components/ui/carousel";
import { DashboardPanel, wobaText } from "@/dashboard/components/dashboard-ds";
import { Badge as DsBadge, Button as DsButton } from "@woba/ds-web-next";

export type MessageVariant = "default" | "monetary" | "percentage" | "time";

type MessageConfig = {
  tag: string;
  title: string;
  description?: string;
};

export type ExceededCreditsCardHookResult = ReturnType<
  typeof useExceededCreditsCard
>;

type ExceededCreditsCardProps = {
  companyId: number;
  companyName?: string;
  userId?: number;
  userEmail?: string;
  carousel?: boolean;
  hookResult?: ExceededCreditsCardHookResult;
  isPackageScreen?: boolean;
};

const VARIANT_ORDER: MessageVariant[] = ["default", "monetary", "percentage"];

export function ExceededCreditsCardComponent({
  companyId,
  companyName,
  userId,
  userEmail,
  carousel = false,
  hookResult: hookResultProp,
  isPackageScreen = false,
}: ExceededCreditsCardProps) {
  const [translate] = useTranslation(["dashboard", "core"]);
  const { onActionClick } = useActionsTrack();
  const hookResultInternal = useExceededCreditsCard({
    companyId,
    companyName,
    userId,
    userEmail,
    onActionClick,
  });

  const {
    state,
    isLoading,
    handleReviewConsumption,
    excessValue,
    excessPercentage,
    excessData,
    messageVariantIndex,
  } = hookResultProp ?? hookResultInternal;

  const [modalOpen, setModalOpen] = useState(false);

  const messageVariant =
    VARIANT_ORDER[messageVariantIndex % VARIANT_ORDER.length] ?? "default";

  const variantEventMap = {
    default: "CLICK_EXCEEDED_CREDITS_CARD_MONTH",
    time: "CLICK_EXCEEDED_CREDITS_CARD_MONTH",
    monetary: "CLICK_EXCEEDED_CREDITS_CARD_MONETARY",
    percentage: "CLICK_EXCEEDED_CREDITS_CARD_PERCENTAGE",
  } as const;

  const handleButtonClick = () => {
    const event =
      variantEventMap[messageVariant] ?? "CLICK_EXCEEDED_CREDITS_CARD_MONTH";
    onActionClick(event, "DASHBOARD");
    const shouldRequestReview =
      state === "exceeded" || state === "analysis_started";
    queueMicrotask(() => {
      if (shouldRequestReview) {
        handleModalRequestReview();
      }
      setModalOpen(true);
    });
  };

  const handleModalRequestReview = () => {
    void handleReviewConsumption();
  };

  /**
   * Message configurations for different variants.
   * Rotates on each reload: default → monetary → percentage.
   */
  const messageConfigs: Record<MessageVariant, MessageConfig> = useMemo(
    () => ({
      default: {
        tag: translate("exceeded-credits-tag-high-consumption"),
        title: translate("exceeded-credits-title-above-contracted"),
        description: undefined,
      },
      monetary: {
        tag: translate("exceeded-credits-tag-high-consumption"),
        title: translate("exceeded-credits-title-monetary", {
          value: excessValue,
        }),
        description: undefined,
      },
      percentage: {
        tag: translate("exceeded-credits-tag-high-consumption"),
        title: translate("exceeded-credits-title-percentage", {
          value: Math.round(excessPercentage * 100),
        }),
        description: undefined,
      },
      time: {
        tag: translate("exceeded-credits-tag-high-consumption"),
        title: translate("exceeded-credits-title-above-contracted"),
        description: undefined,
      },
    }),
    [translate, excessValue, excessPercentage],
  );

  const stateConfig = useMemo(() => {
    switch (state) {
      case "exceeded":
        return {
          buttonText: translate("exceeded-credits-button-review-consumption"),
          buttonIcon: null,
          buttonDisabled: false,
        };

      case "sending":
        return {
          buttonText: translate("exceeded-credits-button-sending-request"),
          buttonIcon: <Loader size={16} className="animate-spin" />,
          buttonDisabled: true,
        };

      case "sent":
        return {
          buttonText: translate("exceeded-credits-button-understand-excess"),
          buttonIcon: null,
          buttonDisabled: false,
          headerBadge: translate("exceeded-credits-badge-review-sent"),
          headerBadgeIcon: <Check size={9} className="text-success-700" />,
          buttonVariant: undefined,
        };

      case "analysis_started":
        return {
          headerBadge: translate("exceeded-credits-badge-analysis-started"),
          headerBadgeIcon: <Play size={14} />,
          buttonText: translate("exceeded-credits-button-review-consumption"),
          buttonIcon: null,
          buttonDisabled: false,
        };

      case "completed":
        return {
          headerBadge: translate("exceeded-credits-badge-adjustment-completed"),
          headerBadgeIcon: <CheckCircle2 size={14} />,
          tag: translate("exceeded-credits-tag-consumption-under-control"),
          title: translate("exceeded-credits-content-package-yielding"),
          icon: <TrendingUp size={20} className="text-green-600" />,
          iconBg: "bg-green-50",
          buttonText: translate("exceeded-credits-button-track-usage"),
          buttonIcon: null,
          buttonDisabled: false,
        };

      default:
        return {
          buttonText: translate("exceeded-credits-button-review-consumption"),
          buttonIcon: null,
          buttonDisabled: false,
        };
    }
  }, [state, translate]);

  // Get current message config based on variant (or state override for completed)
  const currentMessage = stateConfig?.tag
    ? {
        tag: stateConfig.tag,
        title: stateConfig.title!,
        description: undefined,
      }
    : messageConfigs[messageVariant];

  const isButtonDisabled = isLoading;

  if (state === "initial") {
    return null;
  }

  const isCompletedState = state === "completed";
  const iconBgColor = stateConfig?.iconBg ?? "bg-orange-50";
  const iconElement = stateConfig?.icon ?? (
    <AlertTriangle size={20} className="text-orange-500" />
  );

  return (
    <>
      {carousel && isPackageScreen ? (
        <CarouselItem className="basis-[350px]">
          <div className="h-full w-full rounded-[16px] overflow-hidden min-h-[200px] shadow-sm bg-ai-radial">
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-base font-medium text-darkgray-800">
                {translate("exceeded-credits-badge-exceeded-value")}
              </span>
              <BadgeIA
                text={translate("exceeded-credits-badge-woba-ia") as string}
              />
            </div>

            <div className="flex-1 p-[8px] mt-1 rounded-b-2xl">
              <div className="bg-white rounded-[8px] h-full p-[16px] flex flex-col min-h-[180px]">
                {state === "sent" && stateConfig?.headerBadge ? (
                  <div className="inline-flex gap-1 items-center px-3 py-1 mb-3 rounded-xl bg-success-50 w-fit">
                    <span className="flex size-4 items-center justify-center rounded-full text-success-700 border-[1.3px] border-success-700">
                      {stateConfig.headerBadgeIcon}
                    </span>
                    <span className="text-xs font-medium text-success-700">
                      {stateConfig.headerBadge}
                    </span>
                  </div>
                ) : (
                  <div
                    className={`flex justify-center items-center mb-3 w-10 h-10 rounded-full ${iconBgColor}`}
                  >
                    {iconElement}
                  </div>
                )}

                <span
                  className={`py-2 mb-1 text-xs font-normal text-darkgray-400`}
                >
                  {currentMessage.tag}
                </span>

                <h3 className="mb-auto text-2xl font-medium leading-tight text-darkgray-800">
                  {currentMessage.title}
                </h3>

                {currentMessage.description && (
                  <p className="mt-2 mb-4 text-sm text-slate-500">
                    {currentMessage.description}
                  </p>
                )}

                <Button
                  type="button"
                  onClick={handleButtonClick}
                  disabled={stateConfig?.buttonDisabled ?? isButtonDisabled}
                  variant="outline"
                  className={`w-full mt-4 py-2.5 rounded-lg text-sm border-2 font-medium transition-all flex items-center justify-center gap-2 
     ${
       stateConfig?.buttonVariant === "success"
         ? "bg-success-50 text-success-500 border-success-100 hover:bg-success-100 disabled:!bg-success-50 disabled:!text-success-500 disabled:!border-success-100 disabled:opacity-100"
         : "border-aiBlue-500 text-aiBlue-500 hover:bg-aiBlue-500/10 bg-white"
     }
     ${isButtonDisabled ? "opacity-50 cursor-not-allowed" : ""}
   `}
                >
                  {stateConfig?.buttonIcon && (
                    <span className="flex items-center">
                      {stateConfig.buttonIcon}
                    </span>
                  )}
                  {stateConfig?.buttonText}
                </Button>
              </div>
            </div>
          </div>
        </CarouselItem>
      ) : carousel ? (
        <CarouselInsightsItem
          icon={
            <Avatar
              className={cn(
                "pt-1 w-11 h-11 bg-white transition-transform duration-300 group-hover:scale-110",
              )}
            >
              <AvatarImage
                src={BrunaAvatar}
                alt={translate("core:chatbot-bruna-name") as string}
              />
              <AvatarFallback className="text-sm font-semibold bg-white text-aiBlue-800">
                AI
              </AvatarFallback>
            </Avatar>
          }
          content={
            <h2 className="flex gap-2 items-center mt-2 text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-aiBlue-700 via-aiBlue-500 to-aiBlue-200">
              {currentMessage.title}

              <BadgeIA text="Woba AI" />
            </h2>
          }
          emptyContent={currentMessage.description}
          buttonText={stateConfig?.buttonText}
          cardPadding="py-5"
          color={
            stateConfig?.buttonVariant === "success"
              ? {
                  cardBackground: "ai-radial",
                  iconBackground: "success-50",
                  iconText: "success-500",
                  buttonBorder: "success-100",
                  buttonText: "success-500",
                  buttonBorderHover: "success-100",
                  buttonBackgroundHover: "success-100",
                  buttonTextHover: "white",
                }
              : {
                  cardBackground: "ai-radial",
                  iconBackground: "aiBlue-50",
                  iconText: "aiBlue-500",
                  buttonBorder: "aiBlue-500",
                  buttonBackground: "aiBlue-500",
                  buttonText: "white",
                  buttonBorderHover: "aiBlue-700",
                  buttonBackgroundHover: "aiBlue-700",
                  buttonTextHover: "white",
                }
          }
          onClick={handleButtonClick}
        />
      ) : (
        <DashboardPanel
          data-testid="exceeded-credits-card"
          title={currentMessage.tag}
          description={currentMessage.title}
          badge={
            <DsBadge
              variant="intelligence"
              label={translate("dashboard-badge-bruna") as string}
            />
          }
          footer={
            <DsButton
              appearance="secondary"
              size="small"
              style={{ width: "100%" }}
              label={stateConfig?.buttonText as string}
              state={
                state === "sending"
                  ? "loading"
                  : (stateConfig?.buttonDisabled ?? isButtonDisabled)
                    ? "disabled"
                    : "enabled"
              }
              onPress={handleButtonClick}
            />
          }
        >
          {stateConfig?.headerBadge && (
            <div>
              <DsBadge
                variant={isCompletedState ? "success" : "info"}
                label={stateConfig.headerBadge}
              />
            </div>
          )}

          {currentMessage.description && (
            <p style={wobaText.captionMedium}>{currentMessage.description}</p>
          )}
        </DashboardPanel>
      )}

      <ExceededCreditsModalDetails
        open={modalOpen}
        onOpenChange={setModalOpen}
        data={excessData}
      />
    </>
  );
}
